import React, { useMemo } from 'react';
import { Alert, Empty, theme } from 'antd';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DailyDataUsage } from '../services/clarusService';
import { chooseGbUnit, formatGbIn } from '../utils/formatUsage';

/**
 * Daily data usage as stacked Priority / Standard / Non-billable bars, whose
 * sum is the day's total. `optInPriorityGB` is a subset of `priorityGB` — how
 * that priority data was billed, not extra usage — so it is never stacked.
 *
 * With an `overlay`, the chart adds a right-hand cumulative axis: a running
 * cumulative line, an allowance rule, a forecast extension to period end, and
 * the point where cumulative crosses the allowance. The overlay is opt-in;
 * callers that omit it get the plain stacked-bar chart.
 */

interface UsageBar {
  date: string;
  priority: number;
  /** Subset of `priority`, carried for the tooltip — never a stack segment. */
  optIn: number;
  standard: number;
  nonBillable: number;
  total: number;
}

/** Bars oldest-first for a left-to-right time axis. */
export const toUsageBars = (days: DailyDataUsage[]): UsageBar[] =>
  days
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(d => {
      const priority = d.priorityGB ?? 0;
      const standard = d.standardGB ?? 0;
      const nonBillable = d.nonBillableGB ?? 0;
      return {
        date: d.date,
        priority,
        optIn: d.optInPriorityGB ?? 0,
        standard,
        nonBillable,
        total: priority + standard + nonBillable,
      };
    });

/** Which stack segments have any non-zero value across the range. */
export const usedSeries = (bars: UsageBar[]) => ({
  priority: bars.some(b => b.priority > 0),
  standard: bars.some(b => b.standard > 0),
  nonBillable: bars.some(b => b.nonBillable > 0),
});

/** Format a Clarus ISO timestamp as a short "01 Sep" day label. */
export const fmtDay = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', timeZone: 'UTC' });
};

/** Optional cumulative overlay; supplying `allowance` or `cumulative` enables it. */
export interface UsageOverlay {
  allowance?: number | null;
  cumulative?: boolean;
  forecastTotal?: number | null;
  periodEnd?: string | null;
}

interface Props {
  days: DailyDataUsage[];
  height?: number;
  overlay?: UsageOverlay;
}

interface ChartPoint {
  date: string;
  priority?: number;
  standard?: number;
  nonBillable?: number;
  cum?: number;
  forecastCum?: number;
}

const UsageChart: React.FC<Props> = ({ days, height = 280, overlay }) => {
  const { token } = theme.useToken();
  const bars = useMemo(() => toUsageBars(days || []), [days]);
  const show = useMemo(() => usedSeries(bars), [bars]);

  const hasCumulative = !!overlay && (overlay.cumulative || overlay.allowance != null);
  const allowance = overlay?.allowance ?? null;

  const { points, crossDate, cumMax } = useMemo(() => {
    if (bars.length === 0) {
      return { points: [] as ChartPoint[], crossDate: null as string | null, cumMax: 0 };
    }

    let running = 0;
    let cross: string | null = null;
    const actual: ChartPoint[] = bars.map(b => {
      running += b.total;
      if (cross === null && allowance != null && allowance > 0 && running >= allowance) {
        cross = b.date;
      }
      return {
        date: b.date,
        priority: b.priority,
        standard: b.standard,
        nonBillable: b.nonBillable,
        cum: hasCumulative ? running : undefined,
      };
    });

    let peakCum = running;
    if (
      overlay?.forecastTotal != null &&
      overlay.forecastTotal > running &&
      overlay.periodEnd &&
      hasCumulative
    ) {
      const lastActual = new Date(bars[bars.length - 1].date);
      const end = new Date(overlay.periodEnd);
      const msDay = 86_400_000;
      const remainingDays = Math.round((end.getTime() - lastActual.getTime()) / msDay);
      if (remainingDays > 0) {
        const step = (overlay.forecastTotal - running) / remainingDays;
        if (actual.length) actual[actual.length - 1].forecastCum = running;
        for (let i = 1; i <= remainingDays; i++) {
          const d = new Date(lastActual.getTime() + i * msDay);
          actual.push({ date: d.toISOString(), forecastCum: running + step * i });
        }
        peakCum = overlay.forecastTotal;
      }
    }

    return { points: actual, crossDate: cross, cumMax: Math.max(peakCum, allowance ?? 0) };
  }, [bars, allowance, hasCumulative, overlay?.forecastTotal, overlay?.periodEnd]);

  // One unit per axis, chosen from that axis's largest value: daily bars almost
  // always stay GB, while a full period's cumulative may land on TB.
  const dailyUnit = useMemo(
    () => chooseGbUnit(bars.reduce((m, b) => Math.max(m, b.total), 0)),
    [bars]
  );
  const cumUnit = useMemo(
    () => chooseGbUnit(Math.max(cumMax, allowance ?? 0)),
    [cumMax, allowance]
  );
  const axisTick = (u: typeof dailyUnit) => (v: number | string) =>
    (Number(v) / u.divisor).toFixed(u.unit === 'GB' ? 0 : 1);

  if (bars.length === 0) {
    return (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No daily usage in this period" />
    );
  }

  if (bars.length < 2) {
    return (
      <Alert
        type="info"
        showIcon
        message={`${bars[0].total.toFixed(2)} GB used on the first day of this period`}
        description="The daily chart appears once there is more than one day to compare."
      />
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={points}
        margin={{ top: 28, right: hasCumulative ? 8 : 16, bottom: 8, left: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
        <XAxis
          dataKey="date"
          minTickGap={20}
          tick={{ fontSize: 12, fill: token.colorTextSecondary }}
          tickFormatter={fmtDay}
        />
        <YAxis
          yAxisId="gb"
          tickFormatter={axisTick(dailyUnit)}
          tick={{ fontSize: 12, fill: token.colorTextSecondary }}
          label={{
            value: `Daily ${dailyUnit.unit}`,
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 12, fill: token.colorTextSecondary },
          }}
        />
        {hasCumulative && (
          <YAxis
            yAxisId="cum"
            orientation="right"
            domain={[0, Math.ceil((cumMax * 1.05) / 100) * 100 || 'auto']}
            tickFormatter={axisTick(cumUnit)}
            tick={{ fontSize: 12, fill: token.colorTextSecondary }}
            label={{
              value: `Cumulative ${cumUnit.unit}`,
              angle: 90,
              position: 'insideRight',
              style: { fontSize: 12, fill: token.colorTextSecondary },
            }}
          />
        )}
        <Tooltip
          labelFormatter={fmtDay}
          formatter={(value: number | string, name: string) => {
            const onCumAxis = name === 'Cumulative' || name === 'Forecast';
            return [formatGbIn(Number(value), onCumAxis ? cumUnit : dailyUnit), name];
          }}
          contentStyle={{
            background: token.colorBgElevated,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: token.borderRadius,
            color: token.colorText,
          }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {show.priority && (
          <Bar
            yAxisId="gb"
            dataKey="priority"
            name="Priority"
            stackId="gb"
            fill={token.colorPrimary}
            maxBarSize={48}
          />
        )}
        {show.standard && (
          <Bar
            yAxisId="gb"
            dataKey="standard"
            name="Standard"
            stackId="gb"
            fill={token.colorSuccess}
            maxBarSize={48}
          />
        )}
        {show.nonBillable && (
          <Bar
            yAxisId="gb"
            dataKey="nonBillable"
            name="Non-billable"
            stackId="gb"
            fill={token.colorTextTertiary}
            maxBarSize={48}
          />
        )}
        {hasCumulative && overlay?.cumulative !== false && (
          <Line
            yAxisId="cum"
            type="monotone"
            dataKey="cum"
            name="Cumulative"
            stroke={token.colorText}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        )}
        {hasCumulative && points.some(p => p.forecastCum != null) && (
          <Line
            yAxisId="cum"
            type="monotone"
            dataKey="forecastCum"
            name="Forecast"
            stroke={token.colorTextTertiary}
            strokeWidth={2}
            strokeDasharray="5 4"
            dot={false}
            connectNulls
          />
        )}
        {hasCumulative && allowance != null && allowance > 0 && (
          <ReferenceLine
            yAxisId="cum"
            y={allowance}
            stroke={token.colorWarning}
            strokeDasharray="6 3"
            label={{
              value: `Allowance ${formatGbIn(allowance, cumUnit, cumUnit.unit === 'GB' ? 0 : 2)}`,
              position: 'insideTopRight',
              fill: token.colorWarning,
              fontSize: 11,
            }}
          />
        )}
        {hasCumulative && crossDate && (
          <ReferenceLine
            yAxisId="cum"
            x={crossDate}
            stroke={token.colorError}
            strokeDasharray="3 3"
            label={{
              value: `Crossed ${fmtDay(crossDate)}`,
              position: 'top',
              fill: token.colorError,
              fontSize: 11,
            }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default UsageChart;
